const collator = new Intl.Collator();

/**
 * The default cell comparator for {@link LMTable}, used by any column that does
 * not bring its own `sortComparator`.
 *
 * Mirrors the comparators the removed DataGrid used (`gridStringOrNumberComparator`
 * / `gridNumberComparator`, wrapped in `gridNillComparator`): empty values first,
 * numbers compared numerically, booleans false-before-true, everything else
 * collated via `Intl.Collator` so German umlauts sort next to their base letter
 * instead of after "z".
 *
 * TanStack's built-in "alphanumeric" does none of this: it lowercases and compares
 * raw code units, and its internal toString() maps every non-string/non-number to
 * "", which silently makes boolean columns unsortable. "basic" was only applied to
 * columns carrying an explicit `type: "number"`, which TableFactory never sets — so
 * device counts and prices fell through to the string path too.
 *
 * Lives in its own module (rather than inside `LMTable.tsx`) so the pages that page
 * their rows themselves can sort with the very same comparator before slicing,
 * without pulling the whole table component into a util.
 */
export function compareValues(a: unknown, b: unknown): number {
  const aNil = a == null || a === "";
  const bNil = b == null || b === "";
  if (aNil || bNil) return aNil && bNil ? 0 : aNil ? -1 : 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}
